import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "banner-carousel-shell"
}
const _hoisted_2 = ["aria-hidden"]
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = ["aria-selected", "aria-label", "onClick"]
const _hoisted_7 = { key: 1 }
const _hoisted_8 = ["srcset"]
const _hoisted_9 = ["src", "alt"]

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useTheme } from '@/composables/useTheme'
import { useGameStore } from '@/stores/game'


export default /*@__PURE__*/_defineComponent({
  __name: 'HeroBanner',
  setup(__props) {

const { currentTheme, currentThemeName, getAssetPath } = useTheme()
const gameStore = useGameStore()

const resolveThemeAssetPath = (path: string): string => {
  const locale = new URLSearchParams(window.location.search).get('locale') || 'en'
  let resolvedPath = path.replace('{locale}', locale).replace('/locale/en/', `/locale/${locale}/`)

  if (resolvedPath.includes('/themes/')) {
    resolvedPath = resolvedPath.replace(/\/themes\/[^/]+/, `/themes/${currentThemeName.value}`)
  }

  return resolvedPath
}

const uploadedBanner = (assetType: 'large' | 'small'): string => {
  const assets = gameStore.getBetSettings?.theme_assets
  const locale = new URLSearchParams(window.location.search).get('locale') || 'en'
  const localized = assets?.banners?.[locale]?.[assetType]?.url
  const englishFallback = assets?.banners?.en?.[assetType]?.url
  return localized || englishFallback || ''
}

const heroBanner = computed(() => uploadedBanner('large') || getAssetPath('heroBanner') || '/img/images/banners/banner-lg.png')
const heroBannerMobile = computed(() => uploadedBanner('small') || uploadedBanner('large') || getAssetPath('heroBannerMobile') || '/img/images/banners/banner-sm.png')

const activeIndex = ref(0)
const isDragging = ref(false)
const dragStartX = ref(0)
const dragDeltaX = ref(0)
let autoPlayTimer: number | undefined

const carouselSlides = computed(() => {
  const configuredSlides = currentTheme.value.assets.heroBannerSlides
  if (Array.isArray(configuredSlides) && configuredSlides.length > 0) {
    return configuredSlides.map(resolveThemeAssetPath).filter(Boolean)
  }

  return []
})

const isCarousel = computed(() => carouselSlides.value.length > 1)

const trackStyle = computed(() => ({
  transform: `translateX(calc(-${activeIndex.value * 100}% + ${dragDeltaX.value}px))`
}))

const goToSlide = (index: number, restartAutoPlay = true) => {
  const total = carouselSlides.value.length
  if (!total) return
  activeIndex.value = (index + total) % total
  if (restartAutoPlay) resumeCarousel()
}

const nextSlide = () => goToSlide(activeIndex.value + 1)
const previousSlide = () => goToSlide(activeIndex.value - 1)

const pauseCarousel = () => {
  if (autoPlayTimer) {
    window.clearInterval(autoPlayTimer)
    autoPlayTimer = undefined
  }
}

const resumeCarousel = () => {
  pauseCarousel()
  if (isCarousel.value) {
    autoPlayTimer = window.setInterval(nextSlide, 6000)
  }
}

const handlePointerDown = (event: PointerEvent) => {
  if (!isCarousel.value) return
  pauseCarousel()
  isDragging.value = true
  dragStartX.value = event.clientX
  dragDeltaX.value = 0
  ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
}

const handlePointerMove = (event: PointerEvent) => {
  if (!isDragging.value) return
  dragDeltaX.value = event.clientX - dragStartX.value
}

const completeDrag = () => {
  if (!isDragging.value) return
  const threshold = 48
  if (dragDeltaX.value > threshold) {
    goToSlide(activeIndex.value - 1, false)
  } else if (dragDeltaX.value < -threshold) {
    goToSlide(activeIndex.value + 1, false)
  }
  isDragging.value = false
  dragDeltaX.value = 0
  resumeCarousel()
}

const handlePointerUp = () => completeDrag()
const handlePointerCancel = () => {
  isDragging.value = false
  dragDeltaX.value = 0
  resumeCarousel()
}

watch(carouselSlides, (slides) => {
  if (activeIndex.value >= slides.length) activeIndex.value = 0
  if (slides.length > 1) resumeCarousel()
}, { immediate: true })

onMounted(resumeCarousel)
onUnmounted(pauseCarousel)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["hero-banner", { 'is-carousel': isCarousel.value }]),
    onMouseenter: pauseCarousel,
    onMouseleave: resumeCarousel
  }, [
    (isCarousel.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", {
            class: "banner-carousel",
            onPointerdown: handlePointerDown,
            onPointermove: handlePointerMove,
            onPointerup: handlePointerUp,
            onPointercancel: handlePointerCancel
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["banner-track", { dragging: isDragging.value }]),
              style: _normalizeStyle(trackStyle.value)
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(carouselSlides.value, (slide, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: slide,
                  class: "banner-slide",
                  "aria-hidden": index !== activeIndex.value
                }, [
                  _createElementVNode("img", {
                    src: slide,
                    alt: _ctx.$t('forms.lotteryBanner'),
                    draggable: "false"
                  }, null, 8, _hoisted_3)
                ], 8, _hoisted_2))
              }), 128))
            ], 6),
            _createElementVNode("button", {
              class: "carousel-control previous",
              type: "button",
              "aria-label": _ctx.$t('common.previous'),
              onPointerdown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
              onClick: _withModifiers(previousSlide, ["stop","prevent"])
            }, _cache[3] || (_cache[3] = [
              _createElementVNode("svg", {
                viewBox: "0 0 24 24",
                "aria-hidden": "true"
              }, [
                _createElementVNode("path", {
                  d: "M14.5 6.5 9 12l5.5 5.5",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2.5",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                })
              ], -1)
            ]), 40, _hoisted_4),
            _createElementVNode("button", {
              class: "carousel-control next",
              type: "button",
              "aria-label": _ctx.$t('common.next'),
              onPointerdown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
              onClick: _withModifiers(nextSlide, ["stop","prevent"])
            }, _cache[4] || (_cache[4] = [
              _createElementVNode("svg", {
                viewBox: "0 0 24 24",
                "aria-hidden": "true"
              }, [
                _createElementVNode("path", {
                  d: "M9.5 6.5 15 12l-5.5 5.5",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2.5",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                })
              ], -1)
            ]), 40, _hoisted_5)
          ], 32),
          _createElementVNode("div", {
            class: "carousel-dots",
            role: "tablist",
            onPointerdown: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(carouselSlides.value, (_, index) => {
              return (_openBlock(), _createElementBlock("button", {
                key: index,
                class: _normalizeClass(["carousel-dot", { active: index === activeIndex.value }]),
                type: "button",
                role: "tab",
                "aria-selected": index === activeIndex.value,
                "aria-label": `${_ctx.$t('common.view')} ${_ctx.$t('forms.lotteryBanner')} ${index + 1}`,
                onClick: _withModifiers(($event: any) => (goToSlide(index)), ["stop","prevent"])
              }, null, 10, _hoisted_6))
            }), 128))
          ], 32)
        ]))
      : (_openBlock(), _createElementBlock("picture", _hoisted_7, [
          _createElementVNode("source", {
            media: "(min-width: 768px)",
            srcset: heroBanner.value
          }, null, 8, _hoisted_8),
          _createElementVNode("img", {
            src: heroBannerMobile.value,
            alt: _ctx.$t('forms.lotteryBanner')
          }, null, 8, _hoisted_9)
        ]))
  ], 34))
}
}

})